import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

const PERIOD_OPTIONS = [
  { label: "30d", value: 30 },
  { label: "6m", value: 180 },
  { label: "1y", value: 365 },
  { label: "All", value: "all" },
];

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtDate(iso) {
  if (!iso) return iso;
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}

// Period-aware axis label: day/week views show DD/MM; month views (1y / all) show MMM YY.
function fmtAxis(iso, days) {
  if (!iso) return iso;
  const p = String(iso).split("-");
  if (p.length < 3) return iso;
  const monthly = days === "all" || Number(days) > 180;
  return monthly ? `${MONTHS_SHORT[Number(p[1]) - 1] ?? p[1]} ${p[0].slice(2)}` : `${p[2]}/${p[1]}`;
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: highlight ? `2px solid ${BLUE}` : "1px solid #e5e7eb",
        backgroundColor: highlight ? "#eef1f9" : "#f9fafb",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: BLUE, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function RankList({ title, items, color }) {
  if (!items || items.length === 0) return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="textSecondary">No staff transactions in the last 30 days.</Typography>
    </Box>
  );
  const max = items[0]?.totalValue ?? 1;
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 16, textAlign: "right" }}>{i + 1}.</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{item.name || "—"}</Typography>
                <Typography variant="body2" sx={{ color, fontWeight: 700, ml: 1 }}>
                  ${Number(item.totalValue).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
              </Box>
              <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#e5e7eb", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(item.totalValue / max) * 100}%`, backgroundColor: color, borderRadius: 2 }} />
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{item.count} txns</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const recentTxnColumns = [
  {
    field: "TransactionDate",
    headerName: "Date",
    width: 96,
    renderCell: (p) => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
  },
  {
    field: "TransactionValue",
    headerName: "Amount",
    width: 88,
    type: "number",
    align: "right",
    headerAlign: "right",
    renderCell: (p) => (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        ${Number(p.value ?? 0).toFixed(2)}
      </Typography>
    ),
  },
  {
    field: "IsPaid",
    headerName: "Status",
    width: 90,
    align: "center",
    headerAlign: "center",
    renderCell: (p) =>
      p.value ? (
        <Chip label="Paid" size="small" color="success" variant="outlined" />
      ) : (
        <Chip label="Pending" size="small" color="warning" variant="outlined" />
      ),
  },
  {
    field: "StaffName",
    headerName: "Staff",
    flex: 1,
    minWidth: 110,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1 }}>
        {p.value || "—"}
      </Typography>
    ),
  },
];

const ChemistHaloPayTab = ({ chemistUUID, isManager = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async (periodDays) => {
    if (!chemistUUID) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(isManager ? API_PATHS.MANAGER_CHEMIST_HALOPAY : API_PATHS.CHEMIST_HALOPAY, "GET", null, { ChemistUUID: chemistUUID, days: periodDays });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load HaloPay data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID, isManager]);

  useEffect(() => { fetchData(days); }, [fetchData, days]);

  const handlePeriodChange = (newDays) => {
    if (newDays === days) return;
    setDays(newDays);
    setData(null);
  };

  if (loading) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <Typography color="textSecondary">Loading HaloPay data…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const { Metrics, TransactionsPerDay, RecentTransactions, StaffValueBreakdown } = data;
  const txnRows = (RecentTransactions ?? []).map((r, i) => ({ id: r.PaymentRequestID ?? i, ...r }));
  const periodLabel = days === 30 ? "30 days" : days === 180 ? "6 months" : days === 365 ? "12 months" : "all time";
  const bucketWord = days <= 30 ? "day" : days <= 180 ? "week" : "month";
  const barCount = (TransactionsPerDay ?? []).length;
  const chartMinWidth = barCount > 40 ? `${barCount * 30}px` : "100%";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label={`Transactions (${periodLabel})`}
          value={Number(Metrics?.TotalTransactions30d ?? 0).toLocaleString()}
        />
        <StatCard
          label={`Total Value (${periodLabel})`}
          value={`$${Number(Metrics?.TotalValue30d ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          highlight
        />
        <StatCard
          label="Paid"
          value={`${Number(Metrics?.PaidPct ?? 0).toFixed(1)}%`}
          sub={`${Number(Metrics?.TotalPaid30d ?? 0)} of ${Number(Metrics?.TotalTransactions30d ?? 0)}`}
        />
        <StatCard
          label="Avg Transaction"
          value={`$${Number(Metrics?.AvgTransactionValue ?? 0).toFixed(2)}`}
          sub="paid only"
        />
        <StatCard
          label="All-time transactions"
          value={Number(Metrics?.TotalTransactionsAllTime ?? 0).toLocaleString()}
        />
      </Box>

      {/* Chart (left) + Recent transactions (right) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        {/* Left: chart + value by staff */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1, flexWrap: "wrap" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE }}>
                Transactions per {bucketWord} ({periodLabel})
              </Typography>
              <ButtonGroup size="small" disableElevation>
                {PERIOD_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    onClick={() => handlePeriodChange(opt.value)}
                    variant={days === opt.value ? "contained" : "outlined"}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontSize: "0.72rem", px: 1.5, py: 0.4,
                      borderColor: BLUE,
                      color: days === opt.value ? "#fff" : BLUE,
                      backgroundColor: days === opt.value ? BLUE : "transparent",
                      "&:hover": { backgroundColor: days === opt.value ? BLUE : `${BLUE}18` },
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ height: 170, minWidth: chartMinWidth }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={TransactionsPerDay} margin={{ top: 2, right: 4, left: -20, bottom: 18 }}>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => fmtAxis(v, days)}
                      interval="preserveStartEnd"
                      angle={-35}
                      textAnchor="end"
                    />
                    <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <ReTooltip
                      formatter={(v, name) => [v, name === "paid" ? "Paid" : "Pending"]}
                      labelFormatter={(v) => fmtAxis(v, days)}
                      contentStyle={{ fontSize: 11 }}
                    />
                    <Legend
                      formatter={(v) => v === "paid" ? "Paid" : "Pending"}
                      iconSize={10}
                      wrapperStyle={{ fontSize: 11 }}
                    />
                    <Bar dataKey="paid" stackId="a" fill={BLUE} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="unpaid" stackId="a" fill="#93c5fd" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Box>

          <RankList
            title={`Value by staff (${periodLabel})`}
            items={StaffValueBreakdown ?? []}
            color={BLUE}
          />
        </Box>

        {/* Right: recent transactions */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE, mb: 1.5 }}>
            Recent transactions (last 25)
          </Typography>
          {txnRows.length > 0 ? (
            <Box sx={{ height: Math.min(480, 56 + txnRows.length * 36 + 52), width: "100%" }}>
              <DataGrid
                rows={txnRows}
                columns={recentTxnColumns}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                hideFooter
                sx={{ ...pharmacyHaloDataGridSx, width: "100%" }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No transactions in the last 30 days.</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChemistHaloPayTab;
